import { FC, FormEvent, useId } from "react";
import { Box, Dialog, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Button } from "src/domains/shared/components/Button";
import { ModalHeader } from "src/domains/shared/components/ModalHeader";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";
import {
  BusyLabel,
  CAPTION,
  HOVER_TRANSITION_MS,
  REDUCED_MOTION_SX,
} from "src/domains/shared/components/StateTransition";
import {
  MYPAGE_DIALOG_ACTION_MIN_WIDTH_PX,
  MYPAGE_DIALOG_ACTIONS_SX,
  MYPAGE_DIALOG_PAPER_SX,
  MYPAGE_EMAIL_PLACEHOLDER,
  myPageEmailInputSx,
} from "../myPageStyles";

interface TransferOwnerDialogProps {
  open: boolean;
  email: string;
  /**
   * 입력 아래 오류 문구. null 이 아니면 문구를 보이는 데 더해 **실행도 막는다**(실행 버튼 비활성 — Enter 제출도
   * 막힌다). 창은 이 값을 스스로 비우지 않으므로, 호출자가 `onEmailChange` 에서 형식 오류·서버 실패를 함께
   * 비워야 입력을 고친 뒤 다시 실행할 수 있다.
   */
  errorText: string | null;
  transferring: boolean;
  onEmailChange: (value: string) => void;
  onSubmit: () => void;
  onClose: () => void;
}

/** 오류 문구 자리 — 11px/16px 두 줄. 404 안내가 두 원인을 병기해 두 줄까지 간다(멤버 추가 창은 한 줄). */
const ERROR_SLOT_HEIGHT_PX = 32;

/**
 * 소유권 이전 창 — 멤버 추가 창과 같은 규격(`myPageStyles` 의 창 상수)이되, 입력 위에 현재 서버 동작(원래 소유자는 이 프로젝트에서
 * 빠지며 되돌릴 수 없다)을 미리 알리고 실행 버튼은 파괴적 색으로 둔다. 멤버 추가 창을 재사용하지 않는 이유:
 * 경고 문단과 실행 색이 갈려 한 컴포넌트로 묶으면 분기만 는다.
 */
export const TransferOwnerDialog: FC<TransferOwnerDialogProps> = ({
  open,
  email,
  errorText,
  transferring,
  onEmailChange,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation("projects");
  const titleId = useId();
  const warningId = useId();
  const errorId = useId();

  // 이전 중에는 버튼이 스피너를 보여야 하므로 비활성으로 흐리게 두지 않는다.
  const isSubmitBlocked = (!email.trim() || Boolean(errorText)) && !transferring;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!transferring) onSubmit();
  };

  return (
    <Dialog
      open={open}
      onClose={transferring ? undefined : onClose}
      aria-labelledby={titleId}
      aria-describedby={warningId}
      PaperProps={{ sx: MYPAGE_DIALOG_PAPER_SX }}
    >
      <ModalHeader
        title={t("myPageModal.permission.transfer.title", { ns: "projects" })}
        titleId={titleId}
        titleComponent="h2"
        size="small"
        onClose={() => !transferring && onClose()}
      />
      <Box component="form" noValidate onSubmit={handleSubmit}>
        <Box sx={{ padding: "14px 20px 0", display: "flex", flexDirection: "column", gap: "10px" }}>
          {/* 실행 전에 보여야 하는 안내 — 입력 위에 두어 스크롤 없이 읽힌다. */}
          <Typography id={warningId} sx={{ fontSize: 12, lineHeight: 1.5, color: CAPTION }}>
            {t("myPageModal.permission.transfer.warning", { ns: "projects" })}
          </Typography>
          <Box>
            <Box
              component="input"
              type="email"
              autoComplete="off"
              autoFocus
              aria-label={t("myPageModal.permission.transfer.emailLabel", { ns: "projects" })}
              aria-invalid={errorText ? true : undefined}
              aria-describedby={errorText ? errorId : undefined}
              placeholder={MYPAGE_EMAIL_PLACEHOLDER}
              value={email}
              disabled={transferring}
              onChange={(event) => onEmailChange((event.target as HTMLInputElement).value)}
              sx={myPageEmailInputSx(Boolean(errorText))}
            />
            {/* 오류 문구 자리를 두 줄 높이로 미리 잡아 둔다 — 404 안내(두 원인 병기)가 두 줄이라, 오류가 나도 창 크기가 그대로다. */}
            <Typography
              id={errorId}
              role={errorText ? "alert" : undefined}
              sx={{
                minHeight: ERROR_SLOT_HEIGHT_PX,
                marginTop: "4px",
                fontSize: 11,
                lineHeight: "16px",
                color: COLOR_CONSTANTS.error.main,
                opacity: errorText ? 1 : 0,
                transition: `opacity ${HOVER_TRANSITION_MS}ms ease`,
                ...REDUCED_MOTION_SX,
              }}
            >
              {errorText}
            </Typography>
          </Box>
        </Box>
        <Box sx={MYPAGE_DIALOG_ACTIONS_SX}>
          <Button type="button" size="small" emphasis="outlined" intent="inherit" disabled={transferring} onClick={onClose}>
            {t("constants.cancel", { ns: "common" })}
          </Button>
          <Button
            type="submit"
            size="small"
            emphasis="contained"
            intent="error"
            disabled={isSubmitBlocked}
            sx={{ minWidth: MYPAGE_DIALOG_ACTION_MIN_WIDTH_PX }}
          >
            <BusyLabel
              label={t("myPageModal.permission.transfer.submit", { ns: "projects" })}
              busy={transferring}
              busyLabel={t("myPageModal.permission.transfer.transferring", { ns: "projects" })}
              spinnerSize={12}
            />
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
